// por5Utils.js — shared calculation rules for the ปพ.5 report book.
// Rules mirror the school's ปพ.5 Excel workbook (docs/ปพ5.xlsm):
// - each desirable/RTAW item is scored 0-10, totals scaled to 100
// - rating bands: >=85 ดีเยี่ยม, >=70 ดี, >=50 ผ่านเกณฑ์, <50 ไม่ผ่านเกณฑ์
// - a score item passes (ผ) when the mark is >= 50% of its max score
// - whole-year figures = rounded average of the two semester totals, re-rated
import { calculateGrade } from './teachingUtils';

export { calculateGrade };

export const DESIRABLE_LABELS = [
  'รักชาติ ศาสน์ กษัตริย์',
  'ซื่อสัตย์สุจริต',
  'มีวินัย',
  'ใฝ่เรียนรู้',
  'อยู่อย่างพอเพียง',
  'มุ่งมั่นในการทำงาน',
  'รักความเป็นไทย',
  'มีจิตสาธารณะ',
];

export const RTAW_GROUPS = [
  { name: 'การอ่าน', count: 5 },
  { name: 'คิดวิเคราะห์', count: 5 },
  { name: 'เขียนสื่อความ', count: 5 },
];

export const RTAW_LABELS = RTAW_GROUPS.flatMap((g) =>
  Array.from({ length: g.count }, (_, i) => `${g.name} ${i + 1}`)
);

export const ITEM_MAX = 10; // per-item max for desirable/RTAW

export const ATT_MARK = { absent: 'ข', late: 'ส', leave: 'ล' };

export const PASS_RATIO = 0.5; // ผ when mark >= 50% of the item's max score

// Sum 0-10 items and scale to a 0-100 score. Returns null when no item filled.
export function itemsToScore100(items, maxPerItem = ITEM_MAX) {
  const filled = items.filter((v) => v !== null && v !== undefined && v !== '');
  if (filled.length === 0 || items.length === 0) return null;
  const sum = items.reduce((acc, v) => acc + (parseFloat(v) || 0), 0);
  return Math.round((sum / (items.length * maxPerItem)) * 100);
}

// Rating band for a 0-100 assessment score
export function ratingFrom100(score) {
  if (score === null || score === undefined || score === '' || isNaN(score)) return null;
  const s = parseFloat(score);
  if (s >= 85) return 'ดีเยี่ยม';
  if (s >= 70) return 'ดี';
  if (s >= 50) return 'ผ่านเกณฑ์';
  return 'ไม่ผ่านเกณฑ์';
}

// ผ/มผ for a single score item
export function passFail(mark, maxscore) {
  if (mark === null || mark === undefined || mark === '') return null;
  return parseFloat(mark) >= (parseFloat(maxscore) || 0) * PASS_RATIO ? 'ผ' : 'มผ';
}

// Standard competition ranking (ties share a rank, next rank skips).
// entries: [{ key, total }] — withdrawn students / null totals get rank null.
// Returns a Map key → rank.
export function classRank(entries) {
  const ranked = entries
    .filter((e) => e.total !== null && e.total !== undefined)
    .sort((a, b) => b.total - a.total);
  const map = new Map();
  let lastTotal = null;
  let lastRank = 0;
  ranked.forEach((e, i) => {
    const rank = e.total === lastTotal ? lastRank : i + 1;
    map.set(e.key, rank);
    lastTotal = e.total;
    lastRank = rank;
  });
  return map;
}

// Count students at each grade level for the cover page.
// grades: [{ g_grade, g_waiting }] (g_grade = raw 0-100 score).
// Returns { counts: {label: n}, percents: {label: pct}, total }
export const GRADE_LEVELS = ['4', '3.5', '3', '2.5', '2', '1.5', '1', '0', 'ร'];

export function gradeDistribution(grades) {
  const counts = Object.fromEntries(GRADE_LEVELS.map((l) => [l, 0]));
  let total = 0;
  grades.forEach((g) => {
    if (Number(g.g_waiting) === 1) {
      counts['ร'] += 1;
      total += 1;
      return;
    }
    if (g.g_grade === null || g.g_grade === undefined) return;
    const grade = calculateGrade(g.g_grade);
    if (grade === null) return;
    counts[String(grade)] = (counts[String(grade)] || 0) + 1;
    total += 1;
  });
  const percents = {};
  GRADE_LEVELS.forEach((l) => {
    percents[l] = total > 0 ? ((counts[l] / total) * 100).toFixed(2) : '0.00';
  });
  return { counts, percents, total };
}

// Count students at each rating band (for the cover's desirable summary)
export const RATING_LEVELS = ['ดีเยี่ยม', 'ดี', 'ผ่านเกณฑ์', 'ไม่ผ่านเกณฑ์'];

export function ratingDistribution(ratings) {
  const counts = Object.fromEntries(RATING_LEVELS.map((l) => [l, 0]));
  let total = 0;
  ratings.forEach((r) => {
    if (!r) return;
    counts[r] = (counts[r] || 0) + 1;
    total += 1;
  });
  const percents = {};
  RATING_LEVELS.forEach((l) => {
    percents[l] = total > 0 ? ((counts[l] / total) * 100).toFixed(2) : '0.00';
  });
  return { counts, percents, total };
}

// Whole-year figure = rounded average of two semester numbers (Excel rule);
// falls back to whichever semester exists.
export function yearAverage(a, b) {
  const hasA = a !== null && a !== undefined && !isNaN(a);
  const hasB = b !== null && b !== undefined && !isNaN(b);
  if (hasA && hasB) return Math.round((parseFloat(a) + parseFloat(b)) / 2);
  if (hasA) return Math.round(parseFloat(a));
  if (hasB) return Math.round(parseFloat(b));
  return null;
}

// Subject-group (กลุ่มสาระ) from the Thai subject-code first character
const SUBJECT_GROUP_BY_PREFIX = {
  'ท': 'ภาษาไทย',
  'ค': 'คณิตศาสตร์',
  'ว': 'วิทยาศาสตร์และเทคโนโลยี',
  'ส': 'สังคมศึกษา ศาสนา และวัฒนธรรม',
  'พ': 'สุขศึกษาและพลศึกษา',
  'ศ': 'ศิลปะ',
  'ง': 'การงานอาชีพ',
  'อ': 'ภาษาต่างประเทศ',
  'จ': 'ภาษาต่างประเทศ',
  'ญ': 'ภาษาต่างประเทศ',
  'ม': 'ภาษาต่างประเทศ',
};

export function subjectGroupFromCode(code) {
  if (!code) return '';
  return SUBJECT_GROUP_BY_PREFIX[code.trim().charAt(0)] || '';
}

export const THAI_MONTHS_SHORT = [
  '', 'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
  'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.',
];
